/**
 * Farey Sequence based ordering system
 * https://en.wikipedia.org/wiki/Farey_sequence
 * Uses rational numbers (fractions) to represent order values
 * Provides stable, infinite insertion capacity without modifying existing orders
 *
 * The mediant operation: mediant(a/b, c/d) = (a+c)/(b+d)
 * This is always strictly between the two fractions and generates the Stern-Brocot tree
 */

/**
 * Order representation as a rational number
 */
export interface FareyOrder {
  numerator: number;
  denominator: number;
}

// =============================================================================
// Core Operations
// =============================================================================

/**
 * Calculate the mediant of two fractions
 * mediant(a/b, c/d) = (a+c)/(b+d)
 * Result is always strictly between a/b and c/d
 */
export function mediant(a: FareyOrder, b: FareyOrder): FareyOrder {
  return {
    numerator: a.numerator + b.numerator,
    denominator: a.denominator + b.denominator,
  };
}

/**
 * Reduce a fraction to lowest terms
 * Useful for storage optimization and normalization
 */
export function reduceFraction(order: FareyOrder): FareyOrder {
  const gcd = (x: number, y: number): number => (y === 0 ? x : gcd(y, x % y));
  const g = gcd(order.numerator, order.denominator);
  return {
    numerator: order.numerator / g,
    denominator: order.denominator / g,
  };
}

/**
 * Compare two orders
 * Returns: -1 if a < b, 0 if a === b, 1 if a > b
 * Uses cross multiplication to avoid floating point: a/b < c/d iff a*d < b*c
 */
export function compareOrders(a: FareyOrder, b: FareyOrder): -1 | 0 | 1 {
  const aCrossB = a.numerator * b.denominator;
  const bCrossA = b.numerator * a.denominator;

  if (aCrossB < bCrossA) return -1;
  if (aCrossB > bCrossA) return 1;
  return 0;
}

/**
 * Check if two orders represent the same value
 */
export function ordersEqual(a: FareyOrder, b: FareyOrder): boolean {
  return compareOrders(a, b) === 0;
}

/**
 * Convert order to decimal for debugging/display
 */
export function orderToDecimal(order: FareyOrder): number {
  return order.numerator / order.denominator;
}

// =============================================================================
// Serialization
// =============================================================================

/**
 * Serialize order to string for storage
 * Format: "numerator/denominator"
 */
export function serializeOrder(order: FareyOrder): string {
  return `${order.numerator}/${order.denominator}`;
}

/**
 * Deserialize order from string
 * Handles both "num/denom" format and legacy numeric strings
 */
export function deserializeOrder(str: string): FareyOrder {
  if (!str) {
    // throw new Error('Cannot deserialize empty order string');
    console.log('Invalid order encountered: empty string. Defaulting to 1/1.');
    return initializeOrder(1); // default to 1/1 for empty strings
  }

  // Check if it contains a slash (new format)
  if (str.includes('/')) {
    const [numStr, denomStr] = str.split('/');
    const num = parseInt(numStr, 10);
    const denom = parseInt(denomStr, 10);

    if (isNaN(num) || isNaN(denom)) {
      console.log(`Invalid order format: ${str}. Defaulting to 1/1.`);
      return initializeOrder(1);
    }

    if (denom === 0) {
      console.log(
        `Invalid denominator (0) in order: ${str}. Defaulting to 1/1.`,
      );
      return initializeOrder(1);
    }

    return { numerator: num, denominator: denom };
  }

  // Fallback: treat as legacy numeric string (integer converted to fraction)
  const num = parseInt(str, 10);
  if (isNaN(num)) {
    console.log(`Invalid order format: ${str}. Defaulting to 1/1.`);
    return initializeOrder(1);
  }

  return { numerator: num, denominator: 1 };
}

// =============================================================================
// Order Generation
// =============================================================================

/**
 * Boundary orders for comparison and calculation only
 * These are NEVER assigned to actual items, only used as conceptual bounds
 * MIN is 0/1 (lower bound), MAX is 1/0 (upper bound, representing infinity)
 *
 * To insert before the first item (initialized at 1/1):
 *   mediant(MIN_ORDER, 1/1) = mediant(0/1, 1/1) = 1/2
 * To insert after the last item (e.g., at n/1):
 *   mediant(n/1, MAX_ORDER) = mediant(n/1, 1/0) = (n+1)/1
 */
export const MIN_ORDER: FareyOrder = { numerator: 0, denominator: 1 };
export const MAX_ORDER: FareyOrder = { numerator: 1, denominator: 0 };

/**
 * Calculate order between two existing orders
 * For insertion between items with orders a and b
 */
export function getOrderBetween(a: FareyOrder, b: FareyOrder): FareyOrder {
  return mediant(a, b);
}

/**
 * Initialize order at a specific position
 * Useful for creating initial orders without needing neighbors
 * Position must be >= 1, as position 0 would equal MIN_ORDER (0/1) which is reserved as a boundary
 *
 * Examples:
 *   position 1 → 1/1
 *   position 2 → 2/1
 *   position 3 → 3/1
 */
export function initializeOrder(position: number): FareyOrder {
  if (position < 1) {
    throw new Error(
      'Position must be >= 1. Position 0 is reserved as MIN_ORDER boundary.',
    );
  }
  return { numerator: position, denominator: 1 };
}

/**
 * Get order before all existing orders (at the start)
 */
export function getOrderAtStart(firstOrder: FareyOrder): FareyOrder {
  return mediant(MIN_ORDER, firstOrder);
}

/**
 * Get order after all existing orders (at the end)
 */
export function getOrderAtEnd(lastOrder: FareyOrder): FareyOrder {
  return mediant(lastOrder, MAX_ORDER);
}

// =============================================================================
// Array Utilities
// =============================================================================

/**
 * Sort items by their FareyOrder
 */
function sortByFareyOrderObject<T extends { _parsed_farey_order: FareyOrder }>(
  items: T[],
): T[] {
  if (!items.length) return [];
  items.sort((a, b) =>
    compareOrders(a._parsed_farey_order, b._parsed_farey_order),
  );
  return items;
}

/**
 * Sort items by their farey_order string representation
 */
export function sortByFareyOrder<T extends { farey_order: string }>(
  items: T[],
): T[] {
  if (!items.length) return [];
  const withParsed = items.map((item) => ({
    ...item,
    _parsed_farey_order: deserializeOrder(item.farey_order),
  }));
  const sorted = sortByFareyOrderObject(withParsed);
  return sorted.map((item) => {
    const { _parsed_farey_order, ...rest } = item;
    return {
      ...rest,
      farey_order: serializeOrder(_parsed_farey_order),
    } as unknown as T;
  });
}

/**
 * Generate new order for insertion at a specific index in a sorted array
 * Handles all edge cases: insert at start, middle, or end
 */
export function generateOrderForIndex<T extends { order: FareyOrder }>(
  sortedItems: T[],
  insertIndex: number,
): FareyOrder {
  const len = sortedItems.length;

  if (len === 0) {
    // No items, start with a simple order
    return initializeOrder(1);
  }

  if (insertIndex <= 0) {
    // Insert at start
    return getOrderAtStart(sortedItems[0].order);
  }

  if (insertIndex >= len) {
    // Insert at end
    return getOrderAtEnd(sortedItems[len - 1].order);
  }

  // Insert in middle
  return getOrderBetween(
    sortedItems[insertIndex - 1].order,
    sortedItems[insertIndex].order,
  );
}

// =============================================================================
// Next order generation
// =============================================================================

// Helper: get parsed FareyOrder from string
function getParsedFareyOrder(item: { farey_order: string }): FareyOrder {
  return deserializeOrder(item.farey_order);
}

// 1. getFareyOrderUsingIndex
export function getFareyOrderUsingIndex(
  items: Array<{ farey_order: string }>,
  from: number, // -1 when moving between lists
  to: number, // -1 when moving to end of list
  before?: boolean,
): string {
  const len = items.length;
  if (len === 0) return serializeOrder(initializeOrder(1));
  if (to > len || from >= len) throw new Error(`Invalid index for sorting.`);
  let pre =
    to === 0
      ? before
        ? -1
        : to
      : from < 0
        ? to < 0
          ? len > 0
            ? len - 1
            : -1
          : before
            ? to - 1
            : to
        : from < to
          ? before
            ? to - 1
            : to
          : before
            ? to - 1
            : to;
  if (from === pre + 1) pre = from;
  const post =
    from >= 0 && from === pre
      ? pre
      : pre < 0
        ? 0
        : pre < len - 1
          ? pre + 1
          : -1;
  const preOrder = pre < 0 ? MIN_ORDER : getParsedFareyOrder(items[pre]);
  const postOrder =
    post < 0 ? initializeOrder(len + 1) : getParsedFareyOrder(items[post]);
  const newOrder = pre !== post ? mediant(preOrder, postOrder) : preOrder;
  return serializeOrder(newOrder);
}

// 2. getFareyOrderUsingId
export function getFareyOrderUsingId(
  items: Array<{ farey_order: string; id: number }>,
  fromId: number,
  toId: number,
  before?: boolean,
): string {
  const fromIndex = items.findIndex((item) => item.id === fromId);
  const toIndex = items.findIndex((item) => item.id === toId);
  return getFareyOrderUsingIndex(items, fromIndex, toIndex, before);
}

// 3. getFareyOrderForLast
export function getFareyOrderForLast(
  items: Array<{ farey_order: string }>,
  fromIndex: number = -1,
): string {
  if (!items.length) return serializeOrder(initializeOrder(1));
  const sorted = sortByFareyOrder(items);
  return getFareyOrderUsingIndex(sorted, fromIndex, -1);
}

// 4. getFareyOrderByIndex
export function getFareyOrderByIndex(nextIndex: number = -1): string {
  if (nextIndex < 1) return serializeOrder(initializeOrder(1));
  return serializeOrder(initializeOrder(nextIndex + 1));
}

// 5. getNextFareyOrder
export function getNextFareyOrder(
  items: Array<{ farey_order: string }>,
): string {
  if (!items.length) return serializeOrder(initializeOrder(1));
  const sorted = sortByFareyOrder(items);
  return serializeOrder(initializeOrder(sorted.length + 1));
}

// 6. getNextFareyOrderAfterLast
export function getNextFareyOrderAfterLast(
  lastFareyOrder: string | null,
): string {
  if (!lastFareyOrder || lastFareyOrder === '')
    return serializeOrder(initializeOrder(1));
  const items = [{ farey_order: lastFareyOrder, id: 0 }];
  return getNextFareyOrder(items);
}

// =============================================================================
// Validation & Diagnostics
// =============================================================================

/**
 * Validation result type
 */
export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  stats: {
    itemCount: number;
    maxDenominator: number;
    maxNumerator: number;
    duplicateOrders: number;
    outOfOrder: number;
  };
}

/**
 * Validate an array of items with FareyOrder
 * Checks for ordering violations, invalid orders, duplicates, etc.
 */
export function validateOrders<T extends { order: FareyOrder; id?: any }>(
  items: T[],
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  let duplicateOrders = 0;
  let outOfOrder = 0;
  let maxDenominator = 0;
  let maxNumerator = 0;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const order = item.order;

    // Check for valid structure
    if (
      !order ||
      typeof order.numerator !== 'number' ||
      typeof order.denominator !== 'number'
    ) {
      errors.push(
        `Item ${item.id ?? i} has invalid order structure: ${JSON.stringify(order)}`,
      );
      continue;
    }

    // Check for invalid values
    if (order.denominator === 0) {
      errors.push(
        `Item ${item.id ?? i} has denominator of 0: ${serializeOrder(order)}`,
      );
    }

    if (order.denominator < 0 || order.numerator < 0) {
      errors.push(
        `Item ${item.id ?? i} has negative values: ${serializeOrder(order)}`,
      );
    }

    // Track max values
    maxDenominator = Math.max(maxDenominator, order.denominator);
    maxNumerator = Math.max(maxNumerator, order.numerator);

    // Check ordering relative to previous item
    if (i > 0) {
      const prevOrder = items[i - 1].order;
      const cmp = compareOrders(prevOrder, order);

      if (cmp === 0) {
        duplicateOrders++;
        errors.push(
          `Items ${items[i - 1].id ?? i - 1} and ${item.id ?? i} have equal orders: ${serializeOrder(order)}`,
        );
      } else if (cmp > 0) {
        outOfOrder++;
        errors.push(
          `Items out of order: ${items[i - 1].id ?? i - 1} (${serializeOrder(prevOrder)}) > ${item.id ?? i} (${serializeOrder(order)})`,
        );
      }
    }
  }

  // Warning: very large denominators suggest deep nesting
  if (maxDenominator > 1000000) {
    warnings.push(
      `Maximum denominator is very large (${maxDenominator}). Consider normalizing orders.`,
    );
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    stats: {
      itemCount: items.length,
      maxDenominator,
      maxNumerator,
      duplicateOrders,
      outOfOrder,
    },
  };
}

/**
 * Normalize/compress all orders in an array
 * Reduces large numerators/denominators while preserving ordering
 * Useful for maintaining performance over time
 */
export function normalizeOrders<T extends { farey_order: string }>(
  items: T[],
): T[] {
  // Sort to ensure we process in order
  const sorted = sortByFareyOrder(items);

  // Regenerate orders using initial sequence
  const normalized = sorted.map((item, index) => ({
    ...item,
    farey_order: serializeOrder(initializeOrder(index + 1)),
  }));

  return normalized;
}

/**
 * Normalize/compress all orders in an already sorted array
 */
export function normalizeSortedOrders<T extends { farey_order: string }>(
  items: T[],
): T[] {
  // Regenerate orders using initial sequence
  const normalized = items.map((item, index) => ({
    ...item,
    farey_order: serializeOrder(initializeOrder(index + 1)),
  }));

  return normalized;
}

/**
 * Calculate the "depth" of an order in the Stern-Brocot tree
 * Higher depth means more insertions were made at that location
 * Useful for understanding insertion patterns
 */
export function orderDepth(order: FareyOrder): number {
  const reduced = reduceFraction(order);
  return Math.max(
    reduced.numerator.toString(2).length,
    reduced.denominator.toString(2).length,
  );
}

// =============================================================================
// Migration Utilities
// =============================================================================

/**
 * Result of migration operation
 */
export interface MigrationResult<T> {
  success: boolean;
  migratedItems: T[];
  errors: Array<{
    originalOrder: string;
    reason: string;
  }>;
  stats: {
    totalItems: number;
    successfulMigrations: number;
    failedMigrations: number;
    orderPreservationValid: boolean;
  };
}

/**
 * Migrate items with legacy string orders to FareyOrder
 * This is a smart migration that attempts to preserve the original ordering
 *
 * Strategy:
 * 1. If items are already sorted by their string order, assign Farey orders 1/1, 2/1, 3/1, etc.
 * 2. If items are unsorted, sort them first (by string comparison)
 * 3. Collect any items with invalid/unparseable orders as errors
 *
 * @param items - Array of items with a string 'order' field
 * @param orderField - Name of the field containing the order (default: 'order')
 * @returns MigrationResult with migrated items and error information
 */
export function migrateFromLegacyOrders<T extends { order?: string; id?: any }>(
  items: T[],
  orderField: string = 'order',
): MigrationResult<T & { order: FareyOrder }> {
  const errors: Array<{ originalOrder: string; reason: string }> = [];
  const migratedItems: Array<T & { order: FareyOrder }> = [];

  // Filter out items with missing orders
  const itemsWithOrder = items.filter((item) => {
    if (!item.order) {
      errors.push({
        originalOrder: '',
        reason: `Item ${item.id ?? '(unknown)'} has no order`,
      });
      return false;
    }
    return true;
  });

  // Check if items are already sorted by string order
  let isSorted = true;
  for (let i = 1; i < itemsWithOrder.length; i++) {
    if ((itemsWithOrder[i - 1].order ?? '') > (itemsWithOrder[i].order ?? '')) {
      isSorted = false;
      break;
    }
  }

  // Sort by string order if not already sorted
  const itemsToMigrate = isSorted
    ? itemsWithOrder
    : [...itemsWithOrder].sort((a, b) =>
        (a.order ?? '').localeCompare(b.order ?? ''),
      );

  // Assign Farey orders based on position
  itemsToMigrate.forEach((item, index) => {
    try {
      const fareyOrder = initializeOrder(index + 1);
      migratedItems.push({
        ...item,
        order: fareyOrder,
      });
    } catch (e) {
      errors.push({
        originalOrder: item.order ?? '(empty)',
        reason: `Failed to assign Farey order at position ${index + 1}: ${e instanceof Error ? e.message : String(e)}`,
      });
    }
  });

  // Validate that migrated items preserve ordering
  let orderPreservationValid = true;
  for (let i = 1; i < migratedItems.length; i++) {
    if (
      compareOrders(migratedItems[i - 1].order, migratedItems[i].order) >= 0
    ) {
      orderPreservationValid = false;
      break;
    }
  }

  return {
    success: errors.length === 0,
    migratedItems,
    errors,
    stats: {
      totalItems: items.length,
      successfulMigrations: migratedItems.length,
      failedMigrations: errors.length,
      orderPreservationValid,
    },
  };
}
